import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Paper,
  TextField,
  Button,
  CircularProgress,
  Autocomplete,
  LinearProgress,
  Box,
  Typography
} from '@mui/material';
import { motion } from 'framer-motion';
import Sidebar from './Sidebar';
import ResponsiveAppBar from './ResponsiveAppBar';

const Profile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [addressInput, setAddressInput] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    zipCode: ''
  });

  const fetchAddressSuggestions = async (input) => {
    // Replace with your preferred address validation API
    // This is just a placeholder example
    try {
      const response = await fetch(
        `https://api.address-validation-service.com/suggest?input=${encodeURIComponent(input)}`
      );
      const data = await response.json();
      return data.suggestions || [];
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      return [];
    }
  };

  const handleAddressChange = async (event, newValue) => {
    // Update both the input and the profileData
    setAddressInput(newValue);
    setProfile(prev => ({
      ...prev,
      street: newValue
    }));
    
    if (newValue && newValue.length > 2) {
      const suggestions = await fetchAddressSuggestions(newValue);
      setAddressSuggestions(suggestions);
    }
  };

  const handleAddressSelect = (event, value) => {
    if (value) {
      setProfile(prev => ({
        ...prev,
        street: value.street,
        city: value.city,
        state: value.state,
        zipCode: value.zipCode
      }));
    }
  };

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      
      if (!token || !userId) {
        navigate('/login');
        return;
      }

      const response = await fetch(`http://localhost:5001/api/homeowners/profile/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const data = await response.json();
      console.log('Fetched profile data:', data);

      setProfile({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
        phone: data.phone || '',
        street: data.address?.street || '',
        city: data.address?.city || '',
        state: data.address?.state || '',
        zipCode: data.address?.zipCode || ''
      });

      setAddressInput(data.address?.street || '');
      setLoading(false);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile');
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const isProfileComplete = () => {
    const requiredFields = {
      email: profile.email,
      firstName: profile.firstName,
      lastName: profile.lastName,
      phone: profile.phone,
      street: profile.street || profile.address?.street,
      city: profile.city || profile.address?.city,
      state: profile.state || profile.address?.state,
      zipCode: profile.zipCode || profile.address?.zipCode
    };

    const totalFields = Object.keys(requiredFields).length;
    const completedFields = Object.values(requiredFields)
      .filter(field => field && field.trim() !== '').length;
    
    console.log('Profile completion check:', {
      required: requiredFields,
      completed: completedFields,
      total: totalFields,
      percentage: Math.round((completedFields / totalFields) * 100)
    });

    return Math.round((completedFields / totalFields) * 100);
  };

  const profileCompletion = isProfileComplete();
  const canProceedWithEstimates = profileCompletion === 100;

  useEffect(() => {
    localStorage.setItem('profileComplete', JSON.stringify(canProceedWithEstimates));
    console.log('Profile completion status updated:', {
      completion: profileCompletion,
      canProceed: canProceedWithEstimates
    });
  }, [canProceedWithEstimates, profileCompletion]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      
      console.log('Submitting profile update:', profile);

      const response = await fetch(`http://localhost:5001/api/homeowners/profile/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          firstName: profile.firstName,
          lastName: profile.lastName,
          phone: profile.phone,
          address: {
            street: profile.street,
            city: profile.city,
            state: profile.state,
            zipCode: profile.zipCode
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const updatedData = await response.json();
      console.log('Profile updated successfully:', updatedData);
      setMessage('Profile updated successfully!');
      
      // Check completion after update
      const completion = isProfileComplete();
      if (completion === 100) {
        setMessage('Profile complete! You can now approve estimates.');
      }
      
      // Refresh profile data
      await fetchProfile();
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <StyledContainer>
      <Sidebar />
      <ResponsiveAppBar />
      <MainContent>
        <ContentWrapper>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Title>Profile Settings</Title>
            
            <ProgressContainer>
              <Typography variant="h6" gutterBottom>
                Profile Completion: {profileCompletion}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={profileCompletion} 
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: '#ffebe6',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#FF5722',
                    borderRadius: 5
                  }
                }}
              />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                {!canProceedWithEstimates 
                  ? 'Complete your profile to enable estimate approvals'
                  : 'Profile complete! You can now approve estimates'}
              </Typography>
            </ProgressContainer>

            {message && (
              <SuccessMessage>{message}</SuccessMessage>
            )}
            {error && (
              <ErrorMessage>{error}</ErrorMessage>
            )}

            {loading ? (
              <LoadingContainer>
                <CircularProgress />
              </LoadingContainer>
            ) : (
              <StyledPaper elevation={3}>
                <form onSubmit={handleSubmit}>
                  <FormGrid>
                    <TextField
                      label="Email"
                      value={profile.email}
                      disabled
                      fullWidth
                    />
                    <TextField
                      label="First Name"
                      value={profile.firstName}
                      onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                      fullWidth
                    />
                    <TextField
                      label="Last Name"
                      value={profile.lastName}
                      onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                      fullWidth
                    />
                    <TextField
                      label="Phone"
                      value={profile.phone}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                      fullWidth
                    />
                    <Autocomplete
                      freeSolo
                      options={addressSuggestions}
                      getOptionLabel={(option) => 
                        typeof option === 'string' ? option : option.fullAddress
                      }
                      value={addressInput}
                      onChange={handleAddressSelect}
                      onInputChange={(event, newValue) => handleAddressChange(event, newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Street Address"
                          value={profile.street}
                          onChange={(e) => setProfile({ ...profile, street: e.target.value })}
                          fullWidth
                        />
                      )}
                    />
                    <TextField
                      label="City"
                      value={profile.city}
                      onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                      fullWidth
                    />
                    <TextField
                      label="State"
                      value={profile.state}
                      onChange={(e) => setProfile({ ...profile, state: e.target.value })}
                      fullWidth
                    />
                    <TextField
                      label="ZIP Code"
                      value={profile.zipCode}
                      onChange={(e) => setProfile({ ...profile, zipCode: e.target.value })}
                      fullWidth
                    />
                  </FormGrid>
                  
                  <SaveButton
                    type="submit"
                    variant="contained"
                    disabled={saving}
                    fullWidth
                  >
                    {saving ? <CircularProgress size={24} /> : 'Save Changes'}
                  </SaveButton>
                </form>
              </StyledPaper>
            )}
          </motion.div>
        </ContentWrapper>
      </MainContent>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: #f5f5f5;
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 250px;
  padding: 2rem;
  padding-top: calc(64px + 2rem);

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  margin-bottom: 2rem;
  color: #FF5722;
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
`;

const StyledPaper = styled(Paper)`
  padding: 2rem;
  border-radius: 16px;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const ErrorMessage = styled.div`
  color: #f44336;
  margin: 1rem 0;
  padding: 1rem;
  border-radius: 8px;
  background: #FFEBEE;
  text-align: center;
`;

const SuccessMessage = styled.div`
  color: #4CAF50;
  margin: 1rem 0;
  padding: 1rem;
  border-radius: 8px;
  background: #E8F5E9;
  text-align: center;
`;

const SaveButton = styled(Button)`
  && {
    background: #FF5722;
    padding: 1rem;
    font-size: 1.1rem;
    
    &:hover {
      background: #F4511E;
    }

    &:disabled {
      background: #ccc;
    }
  }
`;

const ProgressContainer = styled(Box)`
  margin: 2rem 0;
  padding: 1.5rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

  h6 {
    color: #333;
    margin-bottom: 1rem;
  }
`;

export default Profile;
